import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import {
  Search,
  SlidersHorizontal as Filters,
  Plus,
  Download,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Eye,
  Calendar,
  Package,
  Layers,
  IndianRupee,
  FileCheck,
  Clock,
  X,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
  FileText,
  LayoutDashboard
} from 'lucide-react';
import styles from './WorkOrders.module.css';
import Pagination from '../../components/Common/Pagination/Pagination';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import WorkOrderDrawer from '../../components/Projects/WorkOrderDrawer';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import Footer from '../../components/Common/Footer/Footer';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import PaginatedSelect from '../../components/Common/Select/PaginatedSelect';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const WorkOrders = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importProjectId, setImportProjectId] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const importFileInputRef = useRef(null);
  const mainContentRef = useRef(null);
  const isInitialMount = useRef(true);
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create work orders');
  const canEdit = isAdmin || userPermissions.includes('edit work orders');
  const canDelete = isAdmin || userPermissions.includes('delete work orders');
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerMode, setDrawerMode] = useState('add');
  const [showFilters, setShowFilters] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 10
  });

  const [filters, setFilters] = useState({
    project_id: '',
    unit_id: '',
    status: ''
  });

  const [filterData, setFilterData] = useState({
    projects: [],
    units: []
  });

  const [summary, setSummary] = useState({
    total_items: 0,
    total_quantity: 0,
    total_sales: 0,
    linked_items: 0,
    pending_items: 0
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      if (searchTerm !== debouncedSearch) {
        setPagination(prev => ({ ...prev, current_page: 1 }));
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const [projRes, unitRes] = await Promise.all([
        api.get('/projects?per_page=100'),
        api.get('/units')
      ]);
      setFilterData({
        projects: projRes.data.data.data || [],
        units: unitRes.data.data.data || unitRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const resetFilters = () => {
    setFilters({
      project_id: '',
      unit_id: '',
      status: ''
    });
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const fetchWorkOrders = async () => {
    showLoader();
    try {
      let url = `/work-order-items?page=${pagination.current_page}&per_page=${pagination.per_page}`;
      if (debouncedSearch) url += `&search=${encodeURIComponent(debouncedSearch)}`;
      if (filters.project_id) url += `&project_id=${filters.project_id}`;
      if (filters.unit_id) url += `&unit_id=${filters.unit_id}`;
      if (filters.status) url += `&status=${filters.status}`;

      const response = await api.get(url);
      if (response.data.success) {
        const { data, current_page, last_page, total, per_page } = response.data.data;
        setWorkOrders(data);
        setPagination({ current_page, last_page, total, per_page });
      }
    } catch (error) {
      console.error('Error fetching work orders:', error);
    } finally {
      hideLoader();
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      let url = '/work-order-items/summary?';
      if (debouncedSearch) url += `search=${encodeURIComponent(debouncedSearch)}&`;
      if (filters.project_id) url += `project_id=${filters.project_id}&`;
      if (filters.unit_id) url += `unit_id=${filters.unit_id}&`;
      if (filters.status) url += `status=${filters.status}&`;

      const response = await api.get(url);
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching work orders summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, pagination.per_page, debouncedSearch, filters]);

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filters]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Scroll the main content container to top on page change
    const scrollTimeout = setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);

    return () => clearTimeout(scrollTimeout);
  }, [pagination.current_page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.action-wrapper')) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleActionMenu = (e, index) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
      setOpenMenuIndex(index);
    }
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
    setOpenMenuIndex(null);
  };

  const handleDeleteClick = (item) => {
    setOpenMenuIndex(null);
    Swal.fire({
      html: `
        <div class="custom-icon-wrapper-swal">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Delete Item?</h2>
        <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
          Are you sure you want to delete <strong>${item.item_name}</strong>?
        </p>
        <p style="margin: 0 0 0 0; color: #94a3b8; font-size: 13px;">
          This action cannot be undone and will permanently remove the record.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: 'Yes, Delete Item',
      cancelButtonText: 'No, Cancel',
      customClass: {
        popup: 'custom-swal-popup',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
        actions: 'custom-swal-actions-row'
      },
      buttonsStyling: false
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/work-order-items/${item.id}`);
          if (response.data.success) {
            toast.success('Work order item deleted successfully');
            handleRefresh();
          }
        } catch (error) {
          console.error('Error deleting work order item:', error);
          toast.error(error.response?.data?.message || 'Failed to delete work order item');
        }
      }
    });
  };

  const handleAddClick = () => {
    setSelectedItem(null);
    setDrawerMode('add');
    setIsDrawerOpen(true);
  };

  const handleRefresh = () => {
    fetchWorkOrders();
    fetchSummary();
  };

  const handleDownloadSample = () => {
    // Files in the 'public' folder are served from the root path '/'
    const link = document.createElement('a');
    link.href = '/work_order_sample.xlsx';
    link.download = 'work_order_sample.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
      setImportErrors([]);
    }
  };

  const onDragOverImport = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDropImport = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && (file.type.includes('spreadsheetml') || file.type.includes('csv') || file.name.endsWith('.xlsx') || file.name.endsWith('.csv'))) {
      setImportFile(file);
      setImportErrors([]);
    } else {
      toast.error('Please upload a valid .xlsx or .csv file');
    }
  };

  const handleUploadProcess = async () => {
    if (!importProjectId) {
      toast.error('Please select a project first.');
      return;
    }
    if (!importFile) {
      toast.error('Please select a file to import.');
      return;
    }
    setIsUploading(true);
    try {
      const fd = new FormData();
      fd.append('project_id', importProjectId);
      fd.append('file', importFile);
      const res = await api.post('/work-order-items/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.success) {
        toast.success(res.data.message || 'Items imported successfully');
        setIsImportModalOpen(false);
        setImportFile(null);
        setImportProjectId(null);
        setImportErrors([]);
        handleRefresh();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setImportErrors(error.response.data.errors);
      }
      toast.error(error.response?.data?.message || 'Failed to import items');
    } finally {
      setIsUploading(false);
    }
  };

  const summaryMetrics = [
    {
      label: 'Total Items',
      value: summaryLoading ? '...' : summary.total_items.toLocaleString(),
      icon: <Package size={20} />,
      theme: 'blue'
    },
    {
      label: 'Total Qty',
      value: summaryLoading ? '...' : parseFloat(summary.total_quantity).toLocaleString(),
      subValue: 'units',
      icon: <Layers size={20} />,
      theme: 'green'
    },
    {
      label: 'Total Sales',
      value: summaryLoading ? '...' : `₹${parseFloat(summary.total_sales).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: <IndianRupee size={20} />,
      theme: 'orange',
      highlight: true
    },
    {
      label: 'Linked Items',
      value: summaryLoading ? '...' : summary.linked_items.toLocaleString(),
      subValue: 'in PO',
      icon: <FileCheck size={20} />,
      theme: 'purple'
    },
    {
      label: 'Pending Items',
      value: summaryLoading ? '...' : summary.pending_items.toLocaleString(),
      subValue: 'no PO',
      icon: <Clock size={20} />,
      theme: 'blue'
    }
  ];

  const getStatusClass = (status) => {
    if (!status) return styles['status-active'];
    const s = status.toLowerCase();
    if (s === 'active' || s === 'approved') return styles['status-active'];
    if (s === 'completed' || s === 'done') return styles['status-completed'];
    if (s === 'cancelled' || s === 'rejected') return styles['status-cancelled'];
    return styles['status-pending'];
  };

  return (
    <div className={styles['work-orders-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Work Orders</h1>
              <p>Manage and track project items and materials.</p>
            </div>
            {canCreate && (
              <button className={styles['add-btn']} onClick={handleAddClick}>
                <Plus size={20} />
                <span>Add Work Order Item</span>
              </button>
            )}
          </div>

          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {summaryMetrics[0]?.value} items · ₹{parseFloat(summary.total_sales).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {summaryMetrics.map((metric, index) => (
                <div
                  key={index}
                  className={`${styles['metric-card']} ${styles[metric.theme]} ${metric.highlight ? styles['highlight-card'] : ''}`}
                >
                  <div className={styles['metric-icon-box']}>
                    {metric.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{metric.label}</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{metric.value}</span>
                      {metric.subValue && <span className={styles['metric-sub']}>{metric.subValue}</span>}
                    </div>
                  </div>
                  {metric.highlight && <div className={styles['glow-effect']}></div>}
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search items by name, code or project..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => setPagination(prev => ({ ...prev, per_page: option.value, current_page: 1 }))}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button className={styles['btn-ghost']} onClick={() => setIsImportModalOpen(true)}>
                <Download size={18} />
                <span>Import</span>
              </button>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.project_id || filters.unit_id || filters.status) && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Items</h3>
                  <p>Filter records by project, unit, or status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <PaginatedSelect
                    endpoint="/projects-dropdown"
                    valueKey="id"
                    labelKey="project_name"
                    value={filters.project_id}
                    onChange={(val) => handleFilterChange('project_id', val || '')}
                    placeholder="All Projects"
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Unit</label>
                  <CustomSelect
                    value={filters.unit_id ? {
                      value: filters.unit_id,
                      label: filterData.units.find(u => u.id === filters.unit_id)?.unit_name
                    } : null}
                    onChange={(opt) => handleFilterChange('unit_id', opt ? opt.value : '')}
                    options={filterData.units.map(u => ({ value: u.id, label: u.unit_name }))}
                    placeholder="All Units"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={[styles['custom-table-container'], styles['full-height'], 'card'].join(' ')}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Name</th>
                    <th>Project Code</th>
                    <th>Project Name</th>
                    <th className={styles['text-right']}>Qty</th>
                    <th>Unit</th>
                    <th className={styles['text-right']}>Sales Price</th>
                    <th className={styles['text-right']}>Total</th>
                    <th>Linked PO</th>
                    <th className={styles['text-right']}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.length > 0 ? workOrders.map((item, index) => (
                    <tr key={item.id}>
                      <td className={[styles['code-cell'], styles['font-bold'], styles['link-style']].join(' ')}>#ITEM-{item.id}</td>
                      <td className={[styles['name-cell'], styles['font-bold']].join(' ')}>{item.item_name}</td>
                      <td className={[styles['name-cell'], styles['font-bold'], styles['link-style']].join(' ')}>#{item.project?.project_code}</td>
                      <td className={[styles['name-cell'], styles['font-bold']].join(' ')}>{item.project?.project_name}</td>
                      <td className={[styles['qty-cell'], styles['font-bold'], styles['text-right']].join(' ')}>{item.quantity}</td>
                      <td className={styles['unit-cell']}>{item.unit?.unit_name || '—'}</td>
                      <td className={[styles['price-cell'], styles['font-bold'], styles['text-right']].join(' ')}>{parseFloat(item.sales_price).toLocaleString()}</td>
                      <td className={[styles['total-cell'], styles['font-bold'], styles['text-right']].join(' ')}>{parseFloat(item.total_amount).toLocaleString()}</td>
                      <td className={styles['po-cell']}>
                        {item.purchase_order_items?.length > 0 ? (
                          <div className={styles['po-links']}>
                            {item.purchase_order_items.map((poi, idx) => (
                              <span
                                key={idx}
                                className={styles['po-link']}
                                onClick={() => navigate(`/purchase-orders/${poi.purchase_order?.id}`)}
                              >
                                {poi.purchase_order?.po_number}
                                {idx < item.purchase_order_items.length - 1 ? ', ' : ''}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className={styles['empty-val']}>—</span>
                        )}
                      </td>
                      <td className={styles['actions-cell']}>
                        <div className={`action-wrapper ${styles['action-wrapper']}`}>
                          <button
                            className={`${styles['icon-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                            onClick={(e) => toggleActionMenu(e, index)}
                          >
                            <MoreVertical size={18} />
                          </button>
                          {openMenuIndex === index && (
                            <div
                              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                              style={{ top: menuPosition.top, left: menuPosition.left }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button className={styles['dropdown-item']} onClick={() => navigate(`/projects/work-orders/${item.id}`)}>
                                <Eye size={16} /> View
                              </button>
                              {canEdit && (
                                <button className={styles['dropdown-item']} onClick={() => handleEditClick(item)}>
                                  <Pencil size={16} /> Edit
                                </button>
                              )}
                              {canDelete && (
                                <>
                                  <div className={styles['dropdown-divider']}></div>
                                  <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDeleteClick(item)}>
                                    <Trash2 size={16} /> Delete
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )) : !loading && (
                    <NoData message="No work orders found." colSpan={10} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles['work-order-cards']}>
              {workOrders.length > 0 ? workOrders.map((item, index) => (
                <div key={item.id} className={styles['work-order-card']}>
                  <div className={styles['card-main']}>
                    <div className={styles['card-details']}>
                      <span className={styles['card-code']}>#ITEM-{item.id}</span>
                      <h3>{item.item_name}</h3>
                    </div>
                    <div className={`action-wrapper ${styles['action-wrapper']}`}>
                      <button
                        className={`${styles['icon-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                        onClick={(e) => toggleActionMenu(e, index)}
                      >
                        <MoreVertical size={18} />
                      </button>
                      {openMenuIndex === index && (
                        <div
                          className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                          style={{ top: menuPosition.top, left: menuPosition.left }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button className={styles['dropdown-item']}>
                            <Eye size={16} /> View
                          </button>
                          {canEdit && (
                            <button className={styles['dropdown-item']} onClick={() => handleEditClick(item)}>
                              <Pencil size={16} /> Edit
                            </button>
                          )}
                          {canDelete && (
                            <>
                              <div className={styles['dropdown-divider']}></div>
                              <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDeleteClick(item)}>
                                <Trash2 size={16} /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles['card-stats']}>
                    <div className={styles['stat-item']}>
                      <span className={styles['stat-label']}>Project</span>
                      <span className={styles['stat-value']}>{item.project?.project_name}</span>
                    </div>
                    <div className={styles['stat-item']}>
                      <span className={styles['stat-label']}>Qty</span>
                      <span className={styles['stat-value']}>{item.quantity} {item.unit?.unit_name || ''}</span>
                    </div>
                    {item.purchase_order_items?.length > 0 && (
                      <div className={styles['stat-item']}>
                        <span className={styles['stat-label']}>Linked PO</span>
                        <span className={[styles['stat-value'], styles['po-link']].join(' ')}>
                          {item.purchase_order_items[0].purchase_order?.po_number}
                          {item.purchase_order_items.length > 1 ? ` (+${item.purchase_order_items.length - 1})` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className={styles['card-footer']}>
                    <div className={styles['card-price']}>
                      <span className={styles['price-label']}>Total:</span>
                      <span className={styles['price-value']}>₹ {parseFloat(item.total_amount).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )) : (
                <NoData message="No work orders found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
                onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={pagination.current_page}
              totalItems={pagination.total}
              itemsPerPage={pagination.per_page}
              onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
            />
          </div>

          <Footer />
        </main>
      </div>

      <WorkOrderDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleRefresh}
        mode={drawerMode}
        initialData={selectedItem}
      />

      {isImportModalOpen && (
        <div className={styles['modal-overlay']} onClick={() => setIsImportModalOpen(false)}>
          <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
            <div className={styles['modal-header']}>
              <h3>Import Work Order Items</h3>
              <button 
                className={styles['close-btn']} 
                onClick={() => {
                  setIsImportModalOpen(false);
                  setImportFile(null);
                  setImportProjectId(null);
                  setImportErrors([]);
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div className={styles['modal-body']}>
              <div className={styles['import-field-group']}>
                <label>Select Project </label>
                <PaginatedSelect
                  endpoint="/projects-dropdown"
                  valueKey="id"
                  labelKey="project_name"
                  value={importProjectId}
                  onChange={(val) => setImportProjectId(val || null)}
                  placeholder="Select a project for import"
                />
              </div>

              <div
                className={`${styles['upload-dropzone']} ${importFile ? styles['has-file'] : ''}`}
                onClick={() => !importFile && importFileInputRef.current?.click()}
                onDragOver={onDragOverImport}
                onDrop={onDropImport}
                style={{ cursor: importFile ? 'default' : 'pointer' }}
              >
                <input
                  type="file"
                  ref={importFileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImportFileChange}
                  accept=".xlsx,.csv"
                />
                {importFile ? (
                  <div className={styles['file-selected-info']} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <FileText size={32} style={{ color: '#f59e0b' }} />
                    <p style={{ fontWeight: '600' }}>{importFile.name}</p>
                    <button className={styles['btn-cancel']} onClick={(e) => { e.stopPropagation(); setImportFile(null); }} style={{ padding: '4px 12px', fontSize: '12px' }}>Remove File</button>
                  </div>
                ) : (
                  <>
                    <div className={styles['upload-icon-circle']}>
                      <FileText size={24} />
                    </div>
                    <h4>Drag and drop your .xlsx or .csv file here</h4>
                    <p>or <span className={styles['browse-link']}>click to browse</span> your computer</p>
                  </>
                )}
              </div>

              <button className={styles['template-download-link']} onClick={handleDownloadSample}>
                <Download size={16} />
                <span>Download Sample Template</span>
              </button>

              <div className={styles['info-alert']}>
                <div className={styles['info-icon-wrapper']}>
                  <Plus size={14} style={{ transform: 'rotate(45deg)', background: '#94a3b8', color: 'white', borderRadius: '50%' }} />
                </div>
                <p>Max file size 5MB. Supports up to 500 rows per import.</p>
              </div>

              {importErrors.length > 0 && (
                <div style={{ marginTop: '16px', background: '#fef2f2', border: '1px solid #f87171', borderRadius: '8px', padding: '16px', maxHeight: '250px', overflowY: 'auto' }}>
                  <h4 style={{ color: '#b91c1c', margin: '0 0 10px 0', fontSize: '16px', fontWeight: '700' }}>Import Errors ({importErrors.length}):</h4>
                  <ul style={{ margin: 0, paddingLeft: '24px', color: '#991b1b', fontSize: '15px', lineHeight: '1.5' }}>
                    {importErrors.map((err, idx) => (
                      <li key={idx} style={{ marginBottom: '6px' }}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className={styles['modal-footer']}>
              <button className={styles['btn-cancel']} onClick={() => { setIsImportModalOpen(false); setImportFile(null); setImportProjectId(null); }}>
                Close
              </button>
              <button className={styles['btn-upload']} onClick={handleUploadProcess} disabled={isUploading}>
                {isUploading ? <Loader2 size={18} className={styles['spinner']} /> : <Download size={18} />}
                <span>{isUploading ? 'Uploading...' : 'Upload & Process'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkOrders;

